import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { getUsersByRole, getAccountContactsByRole, createUser, updateUser, deleteUser, resendMentorInvite, checkUserExists, createAccountContact } from "../../Utils/apiServices";
import { stripNumbersFromName } from "../../Utils/nameValidation";
import Button from "../Button";
import Card from "../Card";
import TextField from "../TextField";
import PhoneVerify from "../PhoneVerify";
import { Typography } from "../Typography";
import Avatar from "../Avatar";
import Table from "../Table";

// Parse E.164 phone (e.g. "+916383522927") to { countryCode, nationalNumber } for form display
const parseE164Phone = (phoneStr) => {
  const s = String(phoneStr || "").trim();
  if (!s.startsWith("+")) return null;
  const digits = s.slice(1).replace(/\D/g, "");
  if (!digits.length) return null;
  const dialToCountry = [
    { dial: "91", country: "IN" },
    { dial: "44", country: "GB" },
    { dial: "61", country: "AU" },
    { dial: "1", country: "US" },
  ];
  for (const { dial, country } of dialToCountry) {
    if (digits.startsWith(dial) && digits.length > dial.length) {
      return { countryCode: country, nationalNumber: digits.slice(dial.length) };
    }
  }
  return { countryCode: "US", nationalNumber: digits };
};
const countryCodeToDialPrefix = { US: "+1", CA: "+1", UK: "+44", GB: "+44", AU: "+61", IN: "+91" };
const buildE164Phone = (countryCode, nationalDigits) => {
  const digits = String(nationalDigits || "").replace(/\D/g, "");
  if (!digits) return "";
  const prefix = countryCodeToDialPrefix[countryCode] || countryCodeToDialPrefix.US;
  return prefix + digits;
};

// Corporate Sponsor, Ecosystem Partner, Investor: admin create uses account + contact (same as sign-up), so separate account and SPOC sections
const isAccountContactRole = (r) => r === "corporate_sponsor" || r === "ecosystem_partner" || r === "investor";

// Username and password standards (same as other logins: mentor, student, etc.)
const USERNAME_MIN_LENGTH = 3;
const USERNAME_MAX_LENGTH = 30;
const USERNAME_REGEX = /^(?=.{3,30}$)[A-Za-z0-9](?:[A-Za-z0-9._-]*[A-Za-z0-9])?$/;
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d])\S{8,}$/;
const USERNAME_REQUIREMENT_MSG =
  "Username can only use letters, numbers, periods (.), underscores (_), and hyphens (-), and must start and end with a letter or number (3–30 characters).";
const PASSWORD_REQUIREMENT_MSG =
  "Password must be at least 8 characters and include uppercase, lowercase, number, and a special character (e.g. #).";

const validateUsername = (value) => {
  const u = (value || "").trim().toLowerCase();
  if (!u) return "Username is required.";
  if (u.length < USERNAME_MIN_LENGTH) return "Username must be at least 3 characters.";
  if (u.length > USERNAME_MAX_LENGTH) return "Username must be 30 characters or less.";
  if (!USERNAME_REGEX.test(u)) return USERNAME_REQUIREMENT_MSG;
  return null;
};
const validatePassword = (value) => {
  if (!value || !String(value).trim()) return "Password is required.";
  if (!PASSWORD_REGEX.test(value)) return PASSWORD_REQUIREMENT_MSG;
  return null;
};

const generateSecurePassword = () => {
  const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lower = "abcdefghijklmnopqrstuvwxyz";
  const num = "0123456789";
  const special = "!@#$%&*";
  const pick = (str) => str.charAt(Math.floor(Math.random() * str.length));
  let p = pick(upper) + pick(lower) + pick(num) + pick(special);
  const rest = upper + lower + num + special;
  for (let i = 0; i < 4; i++) p += rest.charAt(Math.floor(Math.random() * rest.length));
  return p.split("").sort(() => Math.random() - 0.5).join("");
};

const FUNDING_TIER_OPTIONS = [
  { value: "5000", label: "Bronze: Ecosystem Supporter - $5,000" },
  { value: "10000", label: "Silver: Talent Discovery - $10,000" },
  { value: "15000", label: "Gold: Market Intelligence - $15,000" },
  { value: "25000", label: "Platinum: Ecosystem Governance - $25,000" },
];

// Support single role (string) or multiple roles (array); when array, fetch each and merge (dedupe by id)
const UserList = ({ role, roles, roleLabel }) => {
  const roleList = roles && Array.isArray(roles) ? roles : (role ? [role] : []);
  const primaryRole = role || (roleList && roleList[0]);
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [accountContactsList, setAccountContactsList] = useState([]); // [{ account, contacts }, ...] for corporate_sponsor, ecosystem_partner, investor
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordContact2, setShowPasswordContact2] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [domainFilter, setDomainFilter] = useState("");
  const [errorMessage, setErrorMessage] = useState({ type: "", text: "" });
  const [emailExistsError, setEmailExistsError] = useState("");
  const [usernameExistsError, setUsernameExistsError] = useState("");
  const [emailExistsErrorContact2, setEmailExistsErrorContact2] = useState("");
  const [usernameExistsErrorContact2, setUsernameExistsErrorContact2] = useState("");
  const [contact2SameAsPrimaryEmail, setContact2SameAsPrimaryEmail] = useState("");
  const [contact2SameAsPrimaryUsername, setContact2SameAsPrimaryUsername] = useState("");
  const [showSecondaryContact, setShowSecondaryContact] = useState(false);
  const [resendingInviteUserId, setResendingInviteUserId] = useState(null);
  const latestEmailRef = useRef("");
  const latestUsernameRef = useRef("");
  const latestEmailRefContact2 = useRef("");
  const latestUsernameRefContact2 = useRef("");
  const [formData, setFormData] = useState(() => ({
    username: "",
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    phone: "",
    phone_country_code: "US",
    role: primaryRole,
    mentor_status: "",
    // Account/company fields (Corporate Sponsor, Ecosystem Partner, Investor create)
    sponsor_name: "",
    legal_entity_name: "",
    website_url: "",
    funding_amount: "",
    contact_role: "",
    contact_email: "",
    contact_phone: "",
    contact_country_code: "US",
    // Optional secondary contact (ecosystem_partner, corporate_sponsor, investor)
    contact_2_first_name: "",
    contact_2_last_name: "",
    contact_2_username: "",
    contact_2_password: "",
    contact_2_contact_role: "",
    contact_2_email: "",
    contact_2_phone: "",
    contact_2_country_code: "US",
  }));

  const fetchUsers = useCallback(async () => {
    if (!roleList.length) return;
    try {
      setLoading(true);
      // corporate_sponsor, ecosystem_partner, investor: use only account-contact list API (GET /account-contact?role=...)
      if (isAccountContactRole(primaryRole)) {
        const results = await Promise.all(roleList.map((r) => getAccountContactsByRole(r)));
        const byAccountId = new Map();
        results.forEach((response) => {
          const body = response?.data; // axios: response.data = API body { success, data: [...] }
          const list = body?.success && Array.isArray(body?.data) ? body.data : [];
          list.forEach((item) => {
            const account = item?.account;
            const contacts = Array.isArray(item?.contacts) ? item.contacts : [];
            if (account?.id != null && !byAccountId.has(account.id)) {
              byAccountId.set(account.id, { account, contacts });
            }
          });
        });
        setAccountContactsList(Array.from(byAccountId.values()));
        setUsers([]);
      } else {
        setAccountContactsList([]);
        if (roleList.length === 1) {
          const response = await getUsersByRole(roleList[0]);
          if (response.success && response?.data?.data) {
            setUsers(Array.isArray(response.data?.data) ? response.data?.data : []);
          }
        } else {
          const results = await Promise.all(roleList.map((r) => getUsersByRole(r)));
          const byId = new Map();
          results.forEach((response) => {
            const list = response?.success && response?.data?.data ? (Array.isArray(response.data.data) ? response.data.data : []) : [];
            list.forEach((u) => {
              if (u?.id != null && !byId.has(u.id)) byId.set(u.id, u);
            });
          });
          setUsers(Array.from(byId.values()));
        }
      }
    } catch (error) {
      console.error(`Error fetching ${roleLabel}:`, error);
    } finally {
      setLoading(false);
    }
  }, [roleList.join(","), roleLabel, primaryRole]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleInputChange = (field, value) => {
    if (field === "first_name" || field === "last_name") {
      value = stripNumbersFromName(value);
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    const emailField = isAccountContactRole(primaryRole) && showCreateForm && !editingId ? "contact_email" : "email";
    if (field === "email" || field === "contact_email") {
      if (field === emailField) {
        latestEmailRef.current = value;
        setEmailExistsError("");
      }
    }
    if (field === "username") {
      latestUsernameRef.current = value;
      setUsernameExistsError("");
    }
    if (field === "contact_2_email") {
      latestEmailRefContact2.current = value;
      setEmailExistsErrorContact2("");
      setContact2SameAsPrimaryEmail("");
    }
    if (field === "contact_2_username") {
      latestUsernameRefContact2.current = value;
      setUsernameExistsErrorContact2("");
      setContact2SameAsPrimaryUsername("");
    }
    if (field === "contact_email") {
      setContact2SameAsPrimaryEmail("");
    }
    if (field === "username") {
      setContact2SameAsPrimaryUsername("");
    }
  };

  const handleEmailBlur = () => {
    const emailValue = isAccountContactRole(primaryRole) && showCreateForm && !editingId
      ? (formData.contact_email || "").trim()
      : (formData.email || "").trim();
    if (!emailValue) {
      setEmailExistsError("");
      return;
    }
    checkUserExists(emailValue)
      .then((res) => {
        if (latestEmailRef.current !== emailValue) return;
        const exists = res?.data?.data?.exists;
        const fieldName = res?.data?.data?.field;
        if (exists && fieldName === "email") {
          setEmailExistsError("This email is already registered.");
        } else {
          setEmailExistsError("");
        }
      })
      .catch(() => {
        if (latestEmailRef.current === emailValue) setEmailExistsError("");
      });
  };

  const handleUsernameBlur = () => {
    const valueToCheck = (formData.username || "").trim().toLowerCase();
    if (!valueToCheck) {
      setUsernameExistsError("");
      return;
    }
    latestUsernameRef.current = valueToCheck;
    checkUserExists(valueToCheck)
      .then((res) => {
        if (latestUsernameRef.current !== valueToCheck) return;
        const exists = res?.data?.data?.exists;
        const fieldName = res?.data?.data?.field;
        if (exists && fieldName === "username") {
          setUsernameExistsError("This username is already taken.");
        } else {
          setUsernameExistsError("");
        }
      })
      .catch(() => {
        if (latestUsernameRef.current === valueToCheck) setUsernameExistsError("");
      });
  };

  const handleContact2EmailBlur = () => {
    const emailValue = (formData.contact_2_email || "").trim().toLowerCase();
    const primaryEmail = (formData.contact_email || "").trim().toLowerCase();
    if (!emailValue) {
      setEmailExistsErrorContact2("");
      setContact2SameAsPrimaryEmail("");
      return;
    }
    if (primaryEmail && emailValue === primaryEmail) {
      setContact2SameAsPrimaryEmail("Secondary contact email must be different from primary contact email.");
    } else {
      setContact2SameAsPrimaryEmail("");
    }
    latestEmailRefContact2.current = emailValue;
    checkUserExists(emailValue)
      .then((res) => {
        if (latestEmailRefContact2.current !== emailValue) return;
        const exists = res?.data?.data?.exists;
        const fieldName = res?.data?.data?.field;
        if (exists && fieldName === "email") {
          setEmailExistsErrorContact2("This email is already registered.");
        } else {
          setEmailExistsErrorContact2("");
        }
      })
      .catch(() => {
        if (latestEmailRefContact2.current === emailValue) setEmailExistsErrorContact2("");
      });
  };

  const handleContact2UsernameBlur = () => {
    const valueToCheck = (formData.contact_2_username || "").trim().toLowerCase();
    const primaryUsername = (formData.username || "").trim().toLowerCase();
    if (!valueToCheck) {
      setUsernameExistsErrorContact2("");
      setContact2SameAsPrimaryUsername("");
      return;
    }
    if (primaryUsername && valueToCheck === primaryUsername) {
      setContact2SameAsPrimaryUsername("Secondary contact username must be different from primary contact username.");
    } else {
      setContact2SameAsPrimaryUsername("");
    }
    latestUsernameRefContact2.current = valueToCheck;
    checkUserExists(valueToCheck)
      .then((res) => {
        if (latestUsernameRefContact2.current !== valueToCheck) return;
        const exists = res?.data?.data?.exists;
        const fieldName = res?.data?.data?.field;
        if (exists && fieldName === "username") {
          setUsernameExistsErrorContact2("This username is already taken.");
        } else {
          setUsernameExistsErrorContact2("");
        }
      })
      .catch(() => {
        if (latestUsernameRefContact2.current === valueToCheck) setUsernameExistsErrorContact2("");
      });
  };

  const handleEdit = (user) => {
    const parsedPhone = parseE164Phone(user.phone);
    setEditingId(user.id);
    setShowCreateForm(false);
    setFormData({
      username: user.username || "",
      email: user.email || "",
      password: "",
      first_name: user.first_name || user.firstName || "",
      last_name: user.last_name || user.lastName || "",
      phone: parsedPhone ? parsedPhone.nationalNumber : (user.phone || ""),
      phone_country_code: parsedPhone ? parsedPhone.countryCode : "US",
      role: user.role || primaryRole,
      mentor_status: user.mentor_status || "",
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setShowPassword(false);
    setShowPasswordContact2(false);
    setShowSecondaryContact(false);
    setErrorMessage({ type: "", text: "" });
    setEmailExistsError("");
    setUsernameExistsError("");
    setEmailExistsErrorContact2("");
    setUsernameExistsErrorContact2("");
    setContact2SameAsPrimaryEmail("");
    setContact2SameAsPrimaryUsername("");
    setFormData({
      username: "",
      email: "",
      password: "",
      first_name: "",
      last_name: "",
      phone: "",
      phone_country_code: "US",
      role: primaryRole,
      mentor_status: "",
      sponsor_name: "",
      legal_entity_name: "",
      website_url: "",
      funding_amount: "",
      contact_role: "",
      contact_email: "",
      contact_phone: "",
      contact_country_code: "US",
      contact_2_first_name: "",
      contact_2_last_name: "",
      contact_2_username: "",
      contact_2_password: "",
      contact_2_contact_role: "",
      contact_2_email: "",
      contact_2_phone: "",
      contact_2_country_code: "US",
    });
  };

  const getErrorMessage = (error) => {
    // Check if error message contains email already exists
    const errorString = error?.message || error?.error || error || "";
    if (errorString.toLowerCase().includes("email already exists") || 
        errorString.toLowerCase().includes("user with this email already exists")) {
      return "A user with this email address already exists. Please use a different email address.";
    }
    return errorString || "An error occurred. Please try again.";
  };

  const handleSave = async (userId = null) => {
    try {
      setErrorMessage({ type: "", text: "" });
      const dataToSend = { ...formData };
      if (dataToSend.username != null) {
        dataToSend.username = (dataToSend.username || "").trim().toLowerCase();
      }
      // Build E.164 phone from country code + national number; remove phone_country_code from payload
      const phoneE164 = buildE164Phone(formData.phone_country_code, formData.phone);
      dataToSend.phone = phoneE164 || dataToSend.phone || "";
      delete dataToSend.phone_country_code;
      // Don't send password if it's empty (for updates)
      if (!dataToSend.password) {
        delete dataToSend.password;
      }

      if (userId) {
        // Update existing user
        // Only include mentor_status if role is mentor
        if (role !== "mentor" && dataToSend.mentor_status !== undefined) {
          delete dataToSend.mentor_status;
        }
        const response = await updateUser(userId, dataToSend);
        if (response.success) {
          await fetchUsers();
          handleCancelEdit();
          setErrorMessage({ type: "success", text: `${roleLabel.slice(0, -1)} updated successfully!` });
          setTimeout(() => setErrorMessage({ type: "", text: "" }), 3000);
        } else {
          const errorMsg = getErrorMessage(response.error || response.message || "Unknown error");
          setErrorMessage({ type: "error", text: errorMsg });
        }
      } else {
        // Create new user or account+contact (Corporate Sponsor, Ecosystem Partner, Investor)
        if (isAccountContactRole(role)) {
          // Same flow as sign-up: account (company) + contact (SPOC)
          const sponsorName = (formData.sponsor_name || "").trim();
          const legalEntity = (formData.legal_entity_name || "").trim();
          const website = (formData.website_url || "").trim();
          const firstName = (formData.first_name || "").trim();
          const lastName = (formData.last_name || "").trim();
          const username = (formData.username || "").trim().toLowerCase();
          const password = formData.password;
          const contactRole = (formData.contact_role || "").trim();
          const contactEmail = (formData.contact_email || "").trim().toLowerCase();
          const contactPhoneRaw = (formData.contact_phone || "").replace(/\D/g, "");
          const contactCountryCode = formData.contact_country_code || "US";
          if (!sponsorName || !legalEntity || !website) {
            setErrorMessage({ type: "error", text: "Company name, legal entity name, and corporate portal URL are required." });
            return;
          }
          if (role === "corporate_sponsor" && !formData.funding_amount) {
            setErrorMessage({ type: "error", text: "Partnership tier is required for Corporate Sponsor." });
            return;
          }
          if (!firstName || !lastName || !username || !password || !contactRole || !contactEmail || !contactPhoneRaw) {
            setErrorMessage({ type: "error", text: "All primary contact fields are required (lead name, username, password, designation / title, lead email, direct line)." });
            return;
          }
          const primaryUsernameErr = validateUsername(username);
          if (primaryUsernameErr) {
            setErrorMessage({ type: "error", text: primaryUsernameErr });
            return;
          }
          const primaryPasswordErr = validatePassword(password);
          if (primaryPasswordErr) {
            setErrorMessage({ type: "error", text: primaryPasswordErr });
            return;
          }
          if (emailExistsError) {
            setErrorMessage({ type: "error", text: emailExistsError });
            return;
          }
          if (usernameExistsError) {
            setErrorMessage({ type: "error", text: usernameExistsError });
            return;
          }
          // If secondary contact is shown, all secondary contact fields are required
          if (showSecondaryContact) {
            const c2First = (formData.contact_2_first_name || "").trim();
            const c2Last = (formData.contact_2_last_name || "").trim();
            const c2Username = (formData.contact_2_username || "").trim().toLowerCase();
            const c2Password = formData.contact_2_password;
            const c2Role = (formData.contact_2_contact_role || "").trim();
            const c2Email = (formData.contact_2_email || "").trim().toLowerCase();
            const c2PhoneRaw = (formData.contact_2_phone || "").replace(/\D/g, "");
            if (!c2First || !c2Last || !c2Username || !c2Password || !c2Role || !c2Email || !c2PhoneRaw) {
              setErrorMessage({ type: "error", text: "When adding a secondary partnership representative, all of their fields are required (name, username, password, designation, email, secondary contact number)." });
              return;
            }
            const c2UsernameErr = validateUsername(c2Username);
            if (c2UsernameErr) {
              setErrorMessage({ type: "error", text: `Secondary contact: ${c2UsernameErr}` });
              return;
            }
            const c2PasswordErr = validatePassword(c2Password);
            if (c2PasswordErr) {
              setErrorMessage({ type: "error", text: `Secondary contact: ${c2PasswordErr}` });
              return;
            }
            if (emailExistsErrorContact2) {
              setErrorMessage({ type: "error", text: emailExistsErrorContact2 });
              return;
            }
            if (usernameExistsErrorContact2) {
              setErrorMessage({ type: "error", text: usernameExistsErrorContact2 });
              return;
            }
            if (c2Email === contactEmail) {
              setErrorMessage({ type: "error", text: "Secondary contact email must be different from primary contact email." });
              return;
            }
            if (c2Username === username) {
              setErrorMessage({ type: "error", text: "Secondary contact username must be different from primary contact username." });
              return;
            }
          }
          const phoneE164 = buildE164Phone(contactCountryCode, formData.contact_phone);
          const accountPayload = {
            name: sponsorName,
            legal_entity_name: legalEntity,
            website,
          };
          if (role === "corporate_sponsor" && formData.funding_amount) {
            const amount = Number(formData.funding_amount);
            if (!Number.isNaN(amount) && amount >= 0) accountPayload.funding_amount = amount;
          }
          const accountContactPayload = {
            account: accountPayload,
            contact: {
              email: contactEmail,
              username,
              role,
              password,
              company_role: contactRole,
              first_name: firstName,
              last_name: lastName,
              phone: phoneE164,
            },
          };
          if (showSecondaryContact) {
            const c2First = (formData.contact_2_first_name || "").trim();
            const c2Last = (formData.contact_2_last_name || "").trim();
            const c2Username = (formData.contact_2_username || "").trim().toLowerCase();
            const c2Password = formData.contact_2_password;
            const c2Role = (formData.contact_2_contact_role || "").trim();
            const c2Email = (formData.contact_2_email || "").trim().toLowerCase();
            const c2PhoneE164 = buildE164Phone(formData.contact_2_country_code || "US", formData.contact_2_phone);
            accountContactPayload.contact_2 = {
              email: c2Email,
              username: c2Username,
              role,
              password: c2Password,
              company_role: c2Role,
              first_name: c2First,
              last_name: c2Last,
              phone: c2PhoneE164,
            };
          }
          const response = await createAccountContact(accountContactPayload);
          if (response.success) {
            await fetchUsers();
            setShowCreateForm(false);
            setShowPassword(false);
            setShowPasswordContact2(false);
            setShowSecondaryContact(false);
            setFormData({
              username: "",
              email: "",
              password: "",
              first_name: "",
              last_name: "",
              phone: "",
              phone_country_code: "US",
              role: primaryRole,
              mentor_status: "",
              sponsor_name: "",
              legal_entity_name: "",
              website_url: "",
              funding_amount: "",
              contact_role: "",
              contact_email: "",
              contact_phone: "",
              contact_country_code: "US",
              contact_2_first_name: "",
              contact_2_last_name: "",
              contact_2_username: "",
              contact_2_password: "",
              contact_2_contact_role: "",
              contact_2_email: "",
              contact_2_phone: "",
              contact_2_country_code: "US",
            });
            setErrorMessage({ type: "success", text: `${roleLabel.slice(0, -1)} created successfully!` });
            setTimeout(() => setErrorMessage({ type: "", text: "" }), 3000);
          } else {
            const errorMsg = getErrorMessage(response.error || response.message || response.data?.message || "Unknown error");
            setErrorMessage({ type: "error", text: errorMsg });
          }
          return;
        }
        // Create regular user (mentor, student, etc.)
        if (emailExistsError) {
          setErrorMessage({ type: "error", text: emailExistsError });
          return;
        }
        if (usernameExistsError) {
          setErrorMessage({ type: "error", text: usernameExistsError });
          return;
        }
        if (!dataToSend.password) {
          setErrorMessage({ type: "error", text: "Password is required for new users" });
          return;
        }
        // New mentors get mentor_status "approved" by default (no dropdown shown on create); mark as admin-added
        if (role === "mentor") {
          dataToSend.mentor_status = "approved";
          dataToSend.admin_added = true;
        }
        const response = await createUser(dataToSend);
        if (response.success) {
          await fetchUsers();
          setShowCreateForm(false);
          setShowPassword(false);
          setFormData({
            username: "",
            email: "",
            password: "",
            first_name: "",
            last_name: "",
            phone: "",
            phone_country_code: "US",
            role: primaryRole,
          });
          setErrorMessage({ type: "success", text: `${roleLabel.slice(0, -1)} created successfully!` });
          setTimeout(() => setErrorMessage({ type: "", text: "" }), 3000);
        } else {
          const errorMsg = getErrorMessage(response.error || response.message || response.data?.message || "Unknown error");
          setErrorMessage({ type: "error", text: errorMsg });
        }
      }
    } catch (error) {
      console.error("Error saving user:", error);
      const errorMsg = getErrorMessage(error?.response?.data?.message || error?.message || error);
      setErrorMessage({ type: "error", text: errorMsg });
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) {
      return;
    }

    try {
      setErrorMessage({ type: "", text: "" });
      const response = await deleteUser(userId);
      if (response.success) {
        await fetchUsers();
        setErrorMessage({ type: "success", text: `${roleLabel.slice(0, -1)} deleted successfully!` });
        setTimeout(() => setErrorMessage({ type: "", text: "" }), 3000);
      } else {
        const errorMsg = getErrorMessage(response.error || response.message || "Unknown error");
        setErrorMessage({ type: "error", text: errorMsg });
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      const errorMsg = getErrorMessage(error?.response?.data?.message || error?.message || error);
      setErrorMessage({ type: "error", text: errorMsg });
    }
  };

  const handleResendMentorInvite = async (record) => {
    const email = String(record?.email || "").trim();
    if (!email) {
      setErrorMessage({ type: "error", text: "No email address for this mentor." });
      return;
    }
    try {
      setErrorMessage({ type: "", text: "" });
      setResendingInviteUserId(record.id);
      const response = await resendMentorInvite(email);
      if (response.success) {
        const body = response.data;
        const msg =
          (typeof body?.message === "string" && body.message) ||
          (typeof body?.data?.message === "string" && body.data.message) ||
          "Invite sent successfully.";
        setErrorMessage({ type: "success", text: msg });
        setTimeout(() => setErrorMessage({ type: "", text: "" }), 3000);
      } else {
        const errorMsg = getErrorMessage(response.error || response.message || response.data?.message || "Unknown error");
        setErrorMessage({ type: "error", text: errorMsg });
      }
    } catch (error) {
      console.error("Error resending mentor invite:", error);
      const errorMsg = getErrorMessage(error?.error || error?.response?.data?.message || error?.message || error);
      setErrorMessage({ type: "error", text: errorMsg });
    } finally {
      setResendingInviteUserId(null);
    }
  };

  const generatePassword = () => {
    setFormData((prev) => ({ ...prev, password: generateSecurePassword() }));
  };

  // Get unique domains from users
  const uniqueDomains = useMemo(() => {
    const domains = new Set();
    users.forEach((user) => {
      // Check multiple possible domain fields
      const domain = user.domain || user.expertise || user.area_of_interest || user.primary_expertise;
      if (domain) {
        if (Array.isArray(domain)) {
          domain.forEach((d) => domains.add(d));
        } else if (typeof domain === "string") {
          domains.add(domain);
        }
      }
      
      // Also check industry field - extract meaningful terms
      if (user.industry) {
        // Split by comma and extract first meaningful phrase (usually the main domain)
        const industryParts = user.industry.split(',').map(part => part.trim());
        industryParts.forEach(part => {
          // Extract first 2-3 words as the main domain term
          const words = part.split(' ').filter(w => w.length > 0);
          if (words.length >= 2) {
            const mainTerm = words.slice(0, 2).join(' ');
            if (mainTerm.length > 3) domains.add(mainTerm);
          }
          // Also add the full part if it's concise
          if (part.length > 3 && part.length < 60) {
            domains.add(part);
          }
        });
      }
      
      // Check functional_experience field
      if (user.functional_experience) {
        // Split by comma and parentheses to extract key terms
        const funcParts = user.functional_experience.split(/[,(]/).map(part => part.trim().replace(/\)/g, ''));
        funcParts.forEach(part => {
          if (part.length > 3 && part.length < 60) {
            domains.add(part);
          }
        });
      }
    });
    return Array.from(domains).sort().map((d) => ({ value: d, label: d }));
  }, [users]);

  // Filter account-contact list (corporate_sponsor, ecosystem_partner, investor) by search
  const filteredAccountContacts = useMemo(() => {
    if (!isAccountContactRole(primaryRole) || !accountContactsList.length) return [];
    let list = accountContactsList;
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      list = list.filter(({ account, contacts }) => {
        const accName = (account?.name || "").toLowerCase();
        const legal = (account?.legal_entity_name || "").toLowerCase();
        const web = (account?.website || "").toLowerCase();
        const primary = contacts[0];
        const secondary = contacts[1];
        const primaryName = primary ? `${(primary.first_name || "").toLowerCase()} ${(primary.last_name || "").toLowerCase()}`.trim() : "";
        const primaryEmail = (primary?.email || "").toLowerCase();
        const secondaryName = secondary ? `${(secondary.first_name || "").toLowerCase()} ${(secondary.last_name || "").toLowerCase()}`.trim() : "";
        const secondaryEmail = (secondary?.email || "").toLowerCase();
        return (
          accName.includes(searchLower) ||
          legal.includes(searchLower) ||
          web.includes(searchLower) ||
          primaryName.includes(searchLower) ||
          primaryEmail.includes(searchLower) ||
          secondaryName.includes(searchLower) ||
          secondaryEmail.includes(searchLower)
        );
      });
    }
    return list;
  }, [accountContactsList, searchTerm, primaryRole]);

  // Filter users based on search term, status, and domain
  const filteredUsers = useMemo(() => {
    let filtered = users;

    // Apply status filter (for mentors)
    if (role === "mentor" && statusFilter) {
      filtered = filtered.filter((user) => {
        const status = user.mentor_status || "pending";
        return status === statusFilter;
      });
    }

    // Apply domain filter
    if (domainFilter) {
      filtered = filtered.filter((user) => {
        // Check primary domain fields
        const domain = user.domain || user.expertise || user.area_of_interest || user.primary_expertise;
        if (domain) {
          if (Array.isArray(domain)) {
            return domain.some(d => d.toLowerCase().includes(domainFilter.toLowerCase()));
          } else if (typeof domain === "string") {
            return domain.toLowerCase().includes(domainFilter.toLowerCase());
          }
        }
        
        // Check industry field
        if (user.industry && user.industry.toLowerCase().includes(domainFilter.toLowerCase())) {
          return true;
        }
        
        // Check functional_experience field
        if (user.functional_experience && user.functional_experience.toLowerCase().includes(domainFilter.toLowerCase())) {
          return true;
        }
        
        return false;
      });
    }

    // Apply search term filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((user) => {
        const firstName = (user.first_name || user.firstName || "").toLowerCase();
        const lastName = (user.last_name || user.lastName || "").toLowerCase();
        const fullName = `${firstName} ${lastName}`.trim().toLowerCase();
        const email = (user.email || "").toLowerCase();
        const phone = (user.phone || "").toLowerCase();
        const username = (user.username || "").toLowerCase();
        
        return (
          fullName.includes(searchLower) ||
          firstName.includes(searchLower) ||
          lastName.includes(searchLower) ||
          email.includes(searchLower) ||
          phone.includes(searchLower) ||
          username.includes(searchLower)
        );
      });
    }

    return filtered;
  }, [users, searchTerm, statusFilter, domainFilter, role]);

  // Define table columns
  const tableColumns = useMemo(() => {
    const columns = [
      {
        header: "Name",
        accessor: "name",
        render: (value, record) => {
          const fullName = record.first_name || record.firstName
            ? `${record.first_name || record.firstName} ${record.last_name || record.lastName || ""}`.trim()
            : record.email || "Unknown User";
          return (
            <div className="flex items-center gap-3">
              <Avatar
                src={record.profile_picture_url}
                firstName={record.first_name || record.firstName}
                lastName={record.last_name || record.lastName}
                email={record.email}
                size="w-10 h-10"
              />
              <div className="flex flex-col min-w-0">
                <button
                  onClick={() => navigate(`/users/${record.id}`)}
                  className="text-sm font-semibold text-blue-600 hover:text-blue-700 hover:underline cursor-pointer text-left truncate"
                >
                  {fullName}
                </button>
                {record.username && (
                  <span className="text-xs text-slate-400 truncate">@{record.username}</span>
                )}
              </div>
            </div>
          );
        },
      },
      {
        header: "Email",
        accessor: "email",
        render: (value) => (
          <Typography as="span" className="text-sm text-tertiary-600">
            {value || "No email"}
          </Typography>
        ),
      },
      {
        header: "Phone",
        accessor: "phone",
        render: (value) => (
          <Typography as="span" className="text-sm text-tertiary-600">
            {value || "-"}
          </Typography>
        ),
      },
    ];

    if (role === "mentor") {
      columns.push({
        header: "Status",
        accessor: "mentor_status",
        render: (value) => {
          const status = value || "pending";
          const config = {
            approved: { bg: "bg-green-50", text: "text-green-700", border: "border-green-200", dot: "bg-green-500" },
            rejected: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", dot: "bg-red-500" },
            suspended: { bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200", dot: "bg-orange-500" },
            submitted: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", dot: "bg-blue-500" },
            pending: { bg: "bg-yellow-50", text: "text-yellow-700", border: "border-yellow-200", dot: "bg-yellow-500" },
          };
          const c = config[status] || config.pending;
          return (
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border capitalize ${c.bg} ${c.text} ${c.border}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
              {status}
            </span>
          );
        },
      });
    }

    columns.push({
      header: "Actions",
      accessor: "actions",
      render: (value, record) => (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            size="small"
            onClick={() => navigate(`/users/${record.id}`)}
            startIcon={<Icon icon="lucide:eye" className="h-4 w-4" />}
          >
            View
          </Button>
          {role === "mentor" && (
            <Button
              variant="secondary"
              size="small"
              onClick={() => handleResendMentorInvite(record)}
              loading={resendingInviteUserId === record.id}
              disabled={!record.email}
              startIcon={<Icon icon="lucide:mail" className="h-4 w-4" />}
            >
              Invite
            </Button>
          )}
          <Button
            variant="secondary"
            size="small"
            onClick={() => handleEdit(record)}
            startIcon={<Icon icon="lucide:pencil" className="h-4 w-4" />}
          >
            Edit
          </Button>
          <button
            onClick={() => handleDelete(record.id)}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors"
          >
            <Icon icon="lucide:trash-2" className="h-3.5 w-3.5" />
            Delete
          </button>
        </div>
      ),
    });

    return columns;
  }, [primaryRole, navigate, role, resendingInviteUserId]);

  // Table columns for account-contact list (corporate_sponsor, ecosystem_partner, investor): account-level + primary / secondary contact
  const accountContactTableColumns = useMemo(() => {
    const cols = [
      {
        header: "Account / Company",
        accessor: "account.name",
        render: (value, record) => {
          const primary = record.primaryContact;
          const name = record.account?.name || "—";
          if (!primary) return <Typography as="span" className="text-sm font-medium text-primary-900">{name}</Typography>;
          return (
            <button
              type="button"
              onClick={() =>
                navigate(`/users/${primary.id}`, {
                  state: {
                    accountContactFromList: {
                      account: record.account,
                      contacts: record.contacts,
                    },
                  },
                })
              }
              className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline cursor-pointer text-left"
            >
              {name}
            </button>
          );
        },
      },
      {
        header: "Legal entity",
        accessor: "account.legal_entity_name",
        render: (value, record) => (
          <Typography as="span" className="text-sm text-tertiary-600">
            {record.account?.legal_entity_name || "—"}
          </Typography>
        ),
      },
      {
        header: "Corporate Portal",
        accessor: "account.website",
        render: (value, record) => (
          <a
            href={record.account?.website || "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:underline truncate max-w-[180px] inline-block"
          >
            {record.account?.website || "—"}
          </a>
        ),
      },
    ];
    if (primaryRole === "corporate_sponsor") {
      cols.push({
        header: "Partnership Tier",
        accessor: "account.funding_amount",
        render: (value, record) => {
          const amount = record.account?.funding_amount;
          if (amount == null) return <Typography as="span" className="text-sm text-slate-400">—</Typography>;
          const num = Number(amount);
          const tierConfig =
            num >= 25000 ? { label: "Platinum", color: "bg-purple-50 text-purple-700 border-purple-200" } :
            num >= 15000 ? { label: "Gold", color: "bg-yellow-50 text-yellow-700 border-yellow-200" } :
            num >= 10000 ? { label: "Silver", color: "bg-slate-50 text-slate-600 border-slate-300" } :
            { label: "Bronze", color: "bg-orange-50 text-orange-700 border-orange-200" };
          return (
            <div className="flex flex-col gap-0.5">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold border w-fit ${tierConfig.color}`}>
                {tierConfig.label}
              </span>
              <span className="text-xs text-slate-400">${num.toLocaleString()}</span>
            </div>
          );
        },
      });
    }
    const contactDisplay = (c) => {
      if (!c) return "—";
      const name = `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email || "—";
      const role = c.company_role ? ` (${c.company_role})` : "";
      return `${name}${role}`;
    };
    cols.push(
      {
        header: "Primary contact",
        accessor: "primaryContact",
        render: (value, record) => (
          <Typography as="span" className="text-sm text-tertiary-700">
            {record.primaryContact ? contactDisplay(record.primaryContact) : "—"}
          </Typography>
        ),
      },
      {
        header: "Secondary contact",
        accessor: "secondaryContact",
        render: (value, record) => (
          <Typography as="span" className="text-sm text-tertiary-600">
            {record.secondaryContact ? contactDisplay(record.secondaryContact) : "—"}
          </Typography>
        ),
      },
      {
        header: "Actions",
        accessor: "actions",
        render: (value, record) => {
          const primary = record.primaryContact;
          if (!primary) return null;
          return (
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="small"
                onClick={() => navigate(`/users/${primary.id}`, {
                  state: { accountContactFromList: { account: record.account, contacts: record.contacts } },
                })}
                startIcon={<Icon icon="lucide:eye" className="h-4 w-4" />}
              >
                View
              </Button>
              <button
                onClick={() => handleDelete(primary.id)}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors"
              >
                <Icon icon="lucide:trash-2" className="h-3.5 w-3.5" />
                Delete
              </button>
            </div>
          );
        },
      }
    );
    return cols;
  }, [primaryRole, navigate]);

  // Table rows for account-contact: each row has id (account.id), account, contacts, primaryContact, secondaryContact
  const tableDataAccountContact = useMemo(() => {
    return filteredAccountContacts.map(({ account, contacts }) => ({
      id: account?.id,
      account,
      contacts,
      primaryContact: contacts[0] || null,
      secondaryContact: contacts[1] || null,
    }));
  }, [filteredAccountContacts]);

  const getFormFields = () => {
    const fields = [
      { name: "username", label: "Username", type: "text", required: true },
      { name: "email", label: "Email", type: "email", required: true },
      { name: "first_name", label: "First Name", type: "text" },
      { name: "last_name", label: "Last Name", type: "text" },
      { name: "phone", label: "Phone", type: "phone" }, // rendered as PhoneVerify with country code + OTP
    ];
    
    // Only add password field when creating (not editing)
    if (!editingId) {
      fields.splice(2, 0, { name: "password", label: "Password", type: "password", required: true });
    }
    
    // Add mentor_status field only for mentor role when editing (not when creating)
    if (role === "mentor" && editingId) {
      fields.push({ 
        name: "mentor_status", 
        label: "Mentor Status", 
        type: "select", 
        options: [
          { value: "pending", label: "Pending" },
          { value: "approved", label: "Approved" },
          { value: "rejected", label: "Rejected" },
        ],
        required: false 
      });
    }
    
    return fields;
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <div className="h-8 w-48 bg-slate-200 rounded-lg animate-pulse" />
          <div className="h-9 w-36 bg-slate-200 rounded-lg animate-pulse" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse" />
          ))}
        </div>
        <div className="h-64 bg-slate-100 rounded-xl animate-pulse" />
      </div>
    );
  }

  const totalCount = isAccountContactRole(primaryRole) ? accountContactsList.length : users.length;

  const mentorStatusCounts = role === "mentor"
    ? users.reduce((acc, u) => {
        const s = u.mentor_status || "pending";
        acc[s] = (acc[s] || 0) + 1;
        return acc;
      }, {})
    : null;

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Typography as="h2" className="text-2xl font-semibold text-primary-900">
            {roleLabel}
          </Typography>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200">
            {totalCount}
          </span>
        </div>
        {role !== "volunteer" && (
          <Button
            onClick={() => {
              setShowCreateForm(true);
              setEditingId(null);
              setErrorMessage({ type: "", text: "" });
              handleCancelEdit();
            }}
            startIcon={<Icon icon="lucide:plus" className="h-4 w-4" />}
          >
            Create {roleLabel.slice(0, -1)}
          </Button>
        )}
      </div>

      {/* Stats Summary Row */}
      {!isAccountContactRole(primaryRole) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-1">
            <Typography as="span" className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total</Typography>
            <Typography as="span" className="text-2xl font-bold text-primary-900">{users.length}</Typography>
          </div>
          {role === "mentor" && mentorStatusCounts ? (
            <>
              <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-1">
                <Typography as="span" className="text-xs font-medium text-slate-500 uppercase tracking-wide">Approved</Typography>
                <Typography as="span" className="text-2xl font-bold text-green-600">{mentorStatusCounts.approved || 0}</Typography>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-1">
                <Typography as="span" className="text-xs font-medium text-slate-500 uppercase tracking-wide">Pending</Typography>
                <Typography as="span" className="text-2xl font-bold text-yellow-600">{mentorStatusCounts.pending || 0}</Typography>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-1">
                <Typography as="span" className="text-xs font-medium text-slate-500 uppercase tracking-wide">Submitted</Typography>
                <Typography as="span" className="text-2xl font-bold text-blue-600">{mentorStatusCounts.submitted || 0}</Typography>
              </div>
            </>
          ) : (
            <>
              <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-1">
                <Typography as="span" className="text-xs font-medium text-slate-500 uppercase tracking-wide">With Email</Typography>
                <Typography as="span" className="text-2xl font-bold text-blue-600">{users.filter(u => u.email).length}</Typography>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-1">
                <Typography as="span" className="text-xs font-medium text-slate-500 uppercase tracking-wide">With Phone</Typography>
                <Typography as="span" className="text-2xl font-bold text-purple-600">{users.filter(u => u.phone).length}</Typography>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-1">
                <Typography as="span" className="text-xs font-medium text-slate-500 uppercase tracking-wide">Filtered</Typography>
                <Typography as="span" className="text-2xl font-bold text-slate-700">{filteredUsers.length}</Typography>
              </div>
            </>
          )}
        </div>
      )}

      {isAccountContactRole(primaryRole) && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-1">
            <Typography as="span" className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total Accounts</Typography>
            <Typography as="span" className="text-2xl font-bold text-primary-900">{accountContactsList.length}</Typography>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-1">
            <Typography as="span" className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total Contacts</Typography>
            <Typography as="span" className="text-2xl font-bold text-blue-600">{accountContactsList.reduce((sum, {contacts}) => sum + contacts.length, 0)}</Typography>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-1">
            <Typography as="span" className="text-xs font-medium text-slate-500 uppercase tracking-wide">Filtered</Typography>
            <Typography as="span" className="text-2xl font-bold text-slate-700">{filteredAccountContacts.length}</Typography>
          </div>
        </div>
      )}

      {/* Error/Success Message */}
      {errorMessage.text && (
        <div
          className={`p-4 rounded-lg border ${
            errorMessage.type === "success"
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
        >
          <div className="flex items-center gap-2">
            <Icon
              icon={
                errorMessage.type === "success"
                  ? "lucide:check-circle"
                  : "lucide:alert-circle"
              }
              className={`h-5 w-5 ${
                errorMessage.type === "success"
                  ? "text-green-600"
                  : "text-red-600"
              }`}
            />
            <Typography as="p" className="text-sm font-medium">
              {errorMessage.text}
            </Typography>
            <button
              onClick={() => setErrorMessage({ type: "", text: "" })}
              className="ml-auto text-current opacity-70 hover:opacity-100"
            >
              <Icon icon="lucide:x" className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {showCreateForm && role !== "volunteer" && (
        <Card className="border border-slate-200">
          <div className="p-6">
            <Typography as="h3" className="text-lg font-semibold text-primary-900 mb-4">
              Create New {roleLabel.slice(0, -1)}
            </Typography>
            {isAccountContactRole(primaryRole) ? (
              <>
                <div className="mb-6">
                  <Typography as="h4" className="text-base font-semibold text-primary-900 mb-1">
                    Company / Account Information
                  </Typography>
                  <Typography as="p" className="text-sm text-tertiary-600 mb-4">
                    Enter the organization or company details.
                  </Typography>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TextField
                      label="Company Name"
                      name="sponsor_name"
                      value={formData.sponsor_name}
                      onChange={(e) => handleInputChange("sponsor_name", e.target.value)}
                      placeholder="Enter the company name"
                      required
                    />
                    <TextField
                      label="Legal Entity Name"
                      name="legal_entity_name"
                      value={formData.legal_entity_name}
                      onChange={(e) => handleInputChange("legal_entity_name", e.target.value)}
                      placeholder="Enter the legal entity name"
                      required
                    />
                    <TextField
                      label="Corporate Portal"
                      name="website_url"
                      type="url"
                      value={formData.website_url}
                      onChange={(e) => handleInputChange("website_url", e.target.value)}
                      placeholder="Enter your corporate portal URL (e.g. https://...)"
                      required
                      className="md:col-span-2"
                    />
                    {primaryRole === "corporate_sponsor" && (
                      <div className="flex flex-col gap-2 md:col-span-2">
                        <div>
                          <label className="text-sm font-medium text-slate-700 text-left">
                            Partnership Tier Selection <span className="text-red-500">*</span>
                          </label>
                          <Typography as="p" className="text-xs text-tertiary-600 text-left mt-1">
                            Tiers determine the level of brand visibility, VIP access, and recognition throughout the program journey.
                          </Typography>
                        </div>
                        <div className="relative">
                          <select
                            name="funding_amount"
                            value={formData.funding_amount || ""}
                            onChange={(e) => handleInputChange("funding_amount", e.target.value)}
                            required
                            className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 pr-10 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none"
                          >
                            <option value="">Select the tier that aligns with your strategic goals.</option>
                            {FUNDING_TIER_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                          <Icon
                            icon="lucide:chevron-down"
                            className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <Typography as="h4" className="text-base font-semibold text-primary-900 mb-1">
                    Direct Engagement Points
                  </Typography>
                  <Typography as="p" className="text-sm text-tertiary-600 mb-4">
                    Ensure we have the right channels to coordinate and collaborate on initiatives.
                  </Typography>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TextField
                      label="Lead representative — first name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={(e) => handleInputChange("first_name", e.target.value)}
                      placeholder="Enter first name"
                      required
                    />
                    <TextField
                      label="Lead representative — last name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={(e) => handleInputChange("last_name", e.target.value)}
                      placeholder="Enter last name"
                      required
                    />
                    <TextField
                      label="Username"
                      name="username"
                      value={formData.username}
                      onChange={(e) => handleInputChange("username", e.target.value)}
                      onBlur={handleUsernameBlur}
                      placeholder="3–30 characters; letters, numbers, . _ -"
                      required
                      error={!!usernameExistsError}
                      helperText={usernameExistsError}
                    />
                    <div className="flex flex-col gap-1 flex-1 md:col-span-2 md:max-w-[50%]">
                      <label className="text-sm font-medium text-slate-700 text-left">
                        Password <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-2">
                        <div className="flex-1 relative">
                          <TextField
                            name="password"
                            type={showPassword ? "text" : "password"}
                            value={formData.password}
                            onChange={(e) => handleInputChange("password", e.target.value)}
                            required
                            placeholder="Min 8 characters, include uppercase, lowercase, number and special character"
                            className={formData.password ? "pr-10" : ""}
                          />
                          {formData.password && (
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-3 bottom-2.5 text-tertiary-600 hover:text-primary-900 focus:outline-none transition-colors"
                            >
                              <Icon icon={showPassword ? "lucide:eye-off" : "lucide:eye"} className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                        <Button variant="secondary" size="medium" onClick={generatePassword} className="whitespace-nowrap">
                          Generate Password
                        </Button>
                      </div>
                    </div>
                    <TextField
                      label="Designation / Title"
                      name="contact_role"
                      value={formData.contact_role}
                      onChange={(e) => handleInputChange("contact_role", e.target.value)}
                      placeholder="Enter the contact role here"
                      required
                    />
                    <TextField
                      label="Lead Email Address"
                      name="contact_email"
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => handleInputChange("contact_email", e.target.value)}
                      onBlur={handleEmailBlur}
                      placeholder="Enter the contact email address here"
                      required
                      error={!!emailExistsError}
                      helperText={emailExistsError}
                    />
                    <div className="flex flex-col gap-1 md:col-span-2">
                      <label className="text-sm font-medium text-slate-700 text-left">
                        Direct Line <span className="text-red-500">*</span>
                      </label>
                      <PhoneVerify
                        showVerify={false}
                        countryCode={formData.contact_country_code}
                        phone={formData.contact_phone}
                        onPhoneChange={(countryCode, phone) => {
                          setFormData((prev) => ({ ...prev, contact_country_code: countryCode, contact_phone: phone }));
                        }}
                        placeholder="Enter the direct line number here"
                        required
                      />
                    </div>
                  </div>
                  {!showSecondaryContact ? (
                    <div className="mt-4">
                      <Button
                        type="button"
                        variant="secondary"
                        size="medium"
                        onClick={() => setShowSecondaryContact(true)}
                        startIcon={<Icon icon="lucide:user-plus" className="h-4 w-4" />}
                      >
                        Add secondary contact
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-6 pt-6 border-t border-slate-200">
                      <div className="flex items-center justify-between mb-4">
                        <Typography as="h4" className="text-base font-semibold text-primary-900">
                          Secondary Partnership Representative (optional)
                        </Typography>
                        <Button
                          type="button"
                          variant="secondary"
                          size="small"
                          onClick={() => {
                            setShowSecondaryContact(false);
                            setFormData((prev) => ({
                              ...prev,
                              contact_2_first_name: "",
                              contact_2_last_name: "",
                              contact_2_username: "",
                              contact_2_password: "",
                              contact_2_contact_role: "",
                              contact_2_email: "",
                              contact_2_phone: "",
                              contact_2_country_code: "US",
                            }));
                            setEmailExistsErrorContact2("");
                            setUsernameExistsErrorContact2("");
                            setContact2SameAsPrimaryEmail("");
                            setContact2SameAsPrimaryUsername("");
                          }}
                          startIcon={<Icon icon="lucide:user-minus" className="h-4 w-4" />}
                        >
                          Remove secondary contact
                        </Button>
                      </div>
                      <Typography as="p" className="text-sm text-tertiary-600 mb-4">
                        Fill all fields below if you want to add a secondary contact for this account.
                      </Typography>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <TextField
                          label="First name"
                          name="contact_2_first_name"
                          value={formData.contact_2_first_name}
                          onChange={(e) => handleInputChange("contact_2_first_name", e.target.value)}
                          placeholder="Enter first name"
                          required
                        />
                        <TextField
                          label="Last name"
                          name="contact_2_last_name"
                          value={formData.contact_2_last_name}
                          onChange={(e) => handleInputChange("contact_2_last_name", e.target.value)}
                          placeholder="Enter last name"
                          required
                        />
                        <TextField
                          label="Username"
                          name="contact_2_username"
                          value={formData.contact_2_username}
                          onChange={(e) => handleInputChange("contact_2_username", e.target.value)}
                          onBlur={handleContact2UsernameBlur}
                          placeholder="3–30 characters; letters, numbers, . _ -"
                          required
                          error={!!usernameExistsErrorContact2 || !!contact2SameAsPrimaryUsername}
                          helperText={contact2SameAsPrimaryUsername || usernameExistsErrorContact2}
                        />
                        <div className="flex flex-col gap-1 flex-1 md:col-span-2 md:max-w-[50%]">
                          <label className="text-sm font-medium text-slate-700 text-left">
                            Password <span className="text-red-500">*</span>
                          </label>
                          <div className="flex gap-2">
                            <div className="flex-1 relative">
                              <TextField
                                name="contact_2_password"
                                type={showPasswordContact2 ? "text" : "password"}
                                value={formData.contact_2_password}
                                onChange={(e) => handleInputChange("contact_2_password", e.target.value)}
                                required
                                placeholder="Min 8 characters, include uppercase, lowercase, number and special character"
                                className={formData.contact_2_password ? "pr-10" : ""}
                              />
                              {formData.contact_2_password && (
                                <button
                                  type="button"
                                  onClick={() => setShowPasswordContact2(!showPasswordContact2)}
                                  className="absolute right-3 bottom-2.5 text-tertiary-600 hover:text-primary-900 focus:outline-none transition-colors"
                                >
                                  <Icon icon={showPasswordContact2 ? "lucide:eye-off" : "lucide:eye"} className="h-5 w-5" />
                                </button>
                              )}
                            </div>
                            <Button
                              variant="secondary"
                              size="medium"
                              onClick={() => setFormData((prev) => ({ ...prev, contact_2_password: generateSecurePassword() }))}
                              className="whitespace-nowrap"
                            >
                              Generate Password
                            </Button>
                          </div>
                        </div>
                        <TextField
                          label="Designation / Title"
                          name="contact_2_contact_role"
                          value={formData.contact_2_contact_role}
                          onChange={(e) => handleInputChange("contact_2_contact_role", e.target.value)}
                          placeholder="Enter the contact role here"
                          required
                        />
                        <TextField
                          label="Secondary Email Address"
                          name="contact_2_email"
                          type="email"
                          value={formData.contact_2_email}
                          onChange={(e) => handleInputChange("contact_2_email", e.target.value)}
                          onBlur={handleContact2EmailBlur}
                          placeholder="Enter the contact email address here"
                          required
                          error={!!emailExistsErrorContact2 || !!contact2SameAsPrimaryEmail}
                          helperText={contact2SameAsPrimaryEmail || emailExistsErrorContact2}
                        />
                        <div className="flex flex-col gap-1 md:col-span-2">
                          <label className="text-sm font-medium text-slate-700 text-left">
                            Secondary Contact Number <span className="text-red-500">*</span>
                          </label>
                          <PhoneVerify
                            showVerify={false}
                            countryCode={formData.contact_2_country_code}
                            phone={formData.contact_2_phone}
                            onPhoneChange={(countryCode, phone) => {
                              setFormData((prev) => ({ ...prev, contact_2_country_code: countryCode, contact_2_phone: phone }));
                            }}
                            placeholder="Enter the secondary contact number here"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {getFormFields().map((field) => {
                if (field.name === "password") {
                  return (
                    <div key={field.name} className="flex items-end gap-2">
                      <div className="flex-1 relative">
                        <div className="relative">
                          <TextField
                            name={field.name}
                            label={field.label}
                            type={showPassword ? "text" : "password"}
                            value={formData[field.name]}
                            onChange={(e) => handleInputChange(field.name, e.target.value)}
                            required={field.required}
                            placeholder={`Enter ${field.label.toLowerCase()}`}
                            className={formData[field.name] ? "pr-10" : ""}
                          />
                          {formData[field.name] && (
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-3 bottom-2.5 text-tertiary-600 hover:text-primary-900 focus:outline-none transition-colors"
                            >
                              <Icon 
                                icon={showPassword ? "lucide:eye-off" : "lucide:eye"} 
                                className="h-5 w-5" 
                              />
                            </button>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="secondary"
                        size="medium"
                        onClick={generatePassword}
                        className="whitespace-nowrap"
                      >
                        Generate Password
                      </Button>
                    </div>
                  );
                }
                
                if (field.type === "select") {
                  return (
                    <div key={field.name} className="flex flex-col gap-1">
                      <label htmlFor={field.name} className="text-sm font-medium text-slate-700 text-left">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                      </label>
                      <div className="relative">
                        <select
                          id={field.name}
                          name={field.name}
                          value={formData[field.name] || ""}
                          onChange={(e) => handleInputChange(field.name, e.target.value)}
                          className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 pr-10 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none"
                        >
                          <option value="">Select {field.label}</option>
                          {field.options?.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <Icon
                          icon="lucide:chevron-down"
                          className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
                        />
                      </div>
                    </div>
                  );
                }
                
                if (field.name === "username") {
                  return (
                    <TextField
                      key={field.name}
                      name={field.name}
                      label={field.label}
                      type={field.type}
                      value={formData[field.name]}
                      onChange={(e) => handleInputChange(field.name, e.target.value)}
                      onBlur={handleUsernameBlur}
                      required={field.required}
                      placeholder={`Enter ${field.label.toLowerCase()}`}
                      error={!!usernameExistsError}
                      helperText={usernameExistsError}
                    />
                  );
                }
                if (field.name === "email") {
                  return (
                    <TextField
                      key={field.name}
                      name={field.name}
                      label={field.label}
                      type={field.type}
                      value={formData[field.name]}
                      onChange={(e) => handleInputChange(field.name, e.target.value)}
                      onBlur={handleEmailBlur}
                      required={field.required}
                      placeholder={`Enter ${field.label.toLowerCase()}`}
                      error={!!emailExistsError}
                      helperText={emailExistsError}
                    />
                  );
                }
                if (field.name === "phone") {
                  return (
                    <div key={field.name} className="flex flex-col gap-1">
                      <label className="text-sm font-medium text-slate-700 text-left">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                      </label>
                      <PhoneVerify
                        showVerify={false}
                        countryCode={formData.phone_country_code}
                        phone={formData.phone}
                        onPhoneChange={(countryCode, phone) => {
                          setFormData((prev) => ({ ...prev, phone_country_code: countryCode, phone }));
                        }}
                        placeholder="Enter phone number"
                      />
                    </div>
                  );
                }
                return (
                  <TextField
                    key={field.name}
                    name={field.name}
                    label={field.label}
                    type={field.type}
                    value={formData[field.name]}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                    required={field.required}
                    placeholder={`Enter ${field.label.toLowerCase()}`}
                  />
                );
              })}
            </div>
            )}
            <div className="flex items-center justify-end gap-3 mt-6">
              <Button variant="secondary" onClick={() => {
                setShowCreateForm(false);
                setShowPassword(false);
                setErrorMessage({ type: "", text: "" });
                setEmailExistsError("");
                setUsernameExistsError("");
              }}>
                Cancel
              </Button>
              <Button onClick={() => handleSave()}>Create</Button>
            </div>
          </div>
        </Card>
      )}

      {editingId && (
        <Card className="border border-slate-200">
          <div className="p-6">
            <Typography as="h3" className="text-base font-semibold text-primary-900 mb-4">
              Edit {roleLabel.slice(0, -1)}
            </Typography>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {getFormFields().map((field) => {
                if (field.type === "select") {
                  return (
                    <div key={field.name} className="flex flex-col gap-1">
                      <label htmlFor={field.name} className="text-sm font-medium text-slate-700 text-left">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                      </label>
                      <div className="relative">
                        <select
                          id={field.name}
                          name={field.name}
                          value={formData[field.name] || ""}
                          onChange={(e) => handleInputChange(field.name, e.target.value)}
                          className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 pr-10 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none"
                        >
                          <option value="">Select {field.label}</option>
                          {field.options?.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <Icon
                          icon="lucide:chevron-down"
                          className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
                        />
                      </div>
                    </div>
                  );
                }
                if (field.name === "phone") {
                  return (
                    <div key={field.name} className="flex flex-col gap-1">
                      <label className="text-sm font-medium text-slate-700 text-left">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                      </label>
                      <PhoneVerify
                        showVerify={false}
                        countryCode={formData.phone_country_code}
                        phone={formData.phone}
                        onPhoneChange={(countryCode, phone) => {
                          setFormData((prev) => ({ ...prev, phone_country_code: countryCode, phone }));
                        }}
                        placeholder="Enter phone number"
                      />
                    </div>
                  );
                }
                return (
                  <TextField
                    key={field.name}
                    name={field.name}
                    label={field.label}
                    type={field.type}
                    value={formData[field.name]}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                    required={field.required && !editingId}
                    placeholder={`Enter ${field.label.toLowerCase()}`}
                  />
                );
              })}
            </div>
            <div className="flex items-center justify-end gap-3 mt-6">
              <Button variant="secondary" onClick={handleCancelEdit}>
                Cancel
              </Button>
              <Button onClick={() => handleSave(editingId)}>Save</Button>
            </div>
          </div>
        </Card>
      )}

      <Card
        className="border border-slate-200 overflow-hidden"
        header={
          <div className="flex flex-col sm:flex-row sm:items-center justify-between w-full gap-3">
            {/* Left: results count + active filter chips */}
            <div className="flex items-center gap-2 flex-wrap">
              <Typography as="span" className="text-sm font-medium text-slate-600">
                {isAccountContactRole(primaryRole)
                  ? `${filteredAccountContacts.length} of ${accountContactsList.length} accounts`
                  : `${filteredUsers.length} of ${users.length} ${roleLabel.toLowerCase()}`}
              </Typography>
              {statusFilter && (
                <button
                  onClick={() => setStatusFilter("")}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
                >
                  Status: {statusFilter}
                  <Icon icon="lucide:x" className="h-3 w-3" />
                </button>
              )}
              {domainFilter && (
                <button
                  onClick={() => setDomainFilter("")}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100"
                >
                  Domain: {domainFilter.length > 20 ? domainFilter.slice(0, 20) + "…" : domainFilter}
                  <Icon icon="lucide:x" className="h-3 w-3" />
                </button>
              )}
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200"
                >
                  "{searchTerm.length > 15 ? searchTerm.slice(0, 15) + "…" : searchTerm}"
                  <Icon icon="lucide:x" className="h-3 w-3" />
                </button>
              )}
            </div>

            {/* Right: filters + search */}
            <div className="flex items-center gap-2 flex-wrap">
              {role === "mentor" && (
                <div className="relative">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-36 px-3 py-1.5 pr-8 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none"
                  >
                    <option value="">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="suspended">Suspended</option>
                    <option value="submitted">Submitted</option>
                  </select>
                  <Icon icon="lucide:chevron-down" className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                </div>
              )}

              {uniqueDomains.length > 0 && (
                <div className="relative">
                  <select
                    value={domainFilter}
                    onChange={(e) => setDomainFilter(e.target.value)}
                    className="w-36 px-3 py-1.5 pr-8 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none"
                  >
                    <option value="">All Domains</option>
                    {uniqueDomains.map((domain) => (
                      <option key={domain.value} value={domain.value}>{domain.label}</option>
                    ))}
                  </select>
                  <Icon icon="lucide:chevron-down" className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                </div>
              )}

              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={`Search ${roleLabel.toLowerCase()}…`}
                  className="w-56 px-3 py-1.5 pl-8 rounded-lg border border-slate-200 bg-white text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue"
                />
                <Icon icon="lucide:search" className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <Icon icon="lucide:x" className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        }
      >
        <Table
          columns={isAccountContactRole(primaryRole) ? accountContactTableColumns : tableColumns}
          data={isAccountContactRole(primaryRole) ? tableDataAccountContact : filteredUsers}
          rowKey="id"
          emptyMessage={
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                <Icon icon="lucide:users" className="h-6 w-6 text-slate-400" />
              </div>
              <div>
                <Typography as="p" className="text-sm font-medium text-slate-600">
                  {isAccountContactRole(primaryRole)
                    ? (accountContactsList.length === 0 ? `No ${roleLabel.toLowerCase()} yet` : `No matches found`)
                    : (users.length === 0 ? `No ${roleLabel.toLowerCase()} yet` : `No matches found`)}
                </Typography>
                <Typography as="p" className="text-xs text-slate-400 mt-0.5">
                  {(searchTerm || statusFilter || domainFilter)
                    ? "Try adjusting your filters or search term."
                    : `Create a ${roleLabel.slice(0, -1).toLowerCase()} to get started.`}
                </Typography>
              </div>
            </div>
          }
        />
      </Card>
    </div>
  );
};

export default UserList;
